import { useState, FormEvent } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { signUp } from '../services/auth';
import { useAuth } from '../store';

/**
 * Sign Up Page
 * Allows users to create a new account
 */
export function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const returnTo = searchParams.get('returnTo');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate inputs
    if (!username.trim() || !email.trim() || !password || !confirmPassword) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (username.trim().length > 20) {
      setError('Username must be 20 characters or less');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      await signUp(email.trim(), password, username.trim());
      
      // Refresh auth context
      await refreshUser();
      
      // Check for pending event join
      const pendingEventJoin = sessionStorage.getItem('pendingEventJoin');
      if (pendingEventJoin) {
        sessionStorage.removeItem('pendingEventJoin');
        // Extract event ID if it's a full URL
        let eventId = pendingEventJoin.trim();
        if (eventId.includes('/join/')) {
          const match = eventId.match(/\/join\/([^\/\s]+)/);
          if (match) {
            eventId = match[1];
          }
        }
        navigate(`/join/${eventId}`);
        return;
      }
      
      // Redirect to return URL or games page
      if (returnTo) {
        navigate(returnTo);
      } else {
        navigate('/games');
      }
    } catch (err: any) {
      console.error('Signup error:', err);
      
      // Handle specific Firebase errors
      let errorMessage = 'Failed to create account. Please try again.';
      if (err.message.includes('email-already-in-use')) {
        errorMessage = 'This email is already registered. Please sign in instead.';
      } else if (err.message.includes('invalid-email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (err.message.includes('weak-password')) {
        errorMessage = 'Password is too weak. Please use at least 6 characters.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
      <Card padding="lg" className="w-full max-w-md">
        <h1 className="text-2xl font-bold text-primary-text mb-6 text-center">
          Create Account
        </h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="username" className="block text-sm font-medium text-primary-text mb-2">
              Username
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Your username (max 20 characters)"
              maxLength={20}
              disabled={loading}
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-primary-text mb-2">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="you@example.com"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-primary-text mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="At least 6 characters"
              disabled={loading}
              required
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-primary-text mb-2">
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Re-enter your password"
              disabled={loading}
              required
            />
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={loading}
            className="w-full"
          >
            {loading ? 'Creating account...' : 'Sign Up'}
          </Button>

          <div className="text-center text-sm text-secondary-text">
            Already have an account?{' '}
            <Link
              to={`/login${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''}`}
              className="text-primary hover:underline"
            >
              Sign in
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}

